import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../hooks';
import * as XLSX from 'xlsx';
import api from '../api';

const TEAL   = '#14b8a6';
const BG     = '#0f1117';
const CARD   = '#1e2329';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const TEXT   = '#f0f2f5';
const RED    = '#ef4444';

const inp = {
  background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: '8px',
  padding: '7px 11px', fontSize: '13px', color: TEXT, outline: 'none',
  width: '100%', boxSizing: 'border-box',
};

// ── column definitions ────────────────────────────────────────────────────────

const MACHINE_COLS = [
  { key: 'name',                  label: 'Name',              req: true,  type: 'text',   hints: ['name','machine name','machine'] },
  { key: 'site',                  label: 'Site / Location',   req: false, type: 'text',   hints: ['site','location','plant','factory','hall'] },
  { key: 'tonnes',                label: 'Tonnage (T)',       req: false, type: 'number', hints: ['tonnes','tonnage','clamp','clamp force','tf','ton','tonne'] },
  { key: 'screw_mm',              label: 'Screw Dia (mm)',    req: false, type: 'number', hints: ['screw','screw_mm','screw diameter','screw dia','screw mm','diameter'] },
  { key: 'ld_ratio',              label: 'L/D Ratio',         req: false, type: 'number', hints: ['l/d','ld','ld ratio','l/d ratio'] },
  { key: 'drive_type',            label: 'Drive Type',        req: false, type: 'text',   hints: ['drive','drive type','machine type','type','drive_type'] },
  { key: 'barrel_cm3',            label: 'Barrel (cm³)',      req: false, type: 'number', hints: ['barrel','barrel_cm3','barrel cm3','injection unit','volume','capacity'] },
  { key: 'platen_type',           label: 'Platen Type',       req: false, type: 'text',   hints: ['platen','platen type','platen_type','platen size'] },
  { key: 'kw',                    label: 'Power (kW)',        req: false, type: 'number', hints: ['kw','power','motor kw','motor power','kilowatt'] },
  { key: 'year',                  label: 'Year',              req: false, type: 'number', hints: ['year','manufacture year','built','year built'] },
  { key: 'chiller_connected',     label: 'Chiller Connected', req: false, type: 'bool',   hints: ['chiller','chiller connected','chiller_connected','water cooled'] },
  { key: 'hot_runner_controller', label: 'Hot Runner Ctrl',   req: false, type: 'bool',   hints: ['hot runner','hot runner controller','hr controller','hot_runner_controller'] },
  { key: 'hot_runner_brand',      label: 'Hot Runner Brand',  req: false, type: 'text',   hints: ['hot runner brand','hr brand','hot runner make','hot_runner_brand'] },
  { key: 'rate_per_hour',         label: 'Rate / Hour',       req: false, type: 'number', hints: ['rate per hour','hourly rate','rate/hour','machine rate','rate_per_hour'] },
  { key: 'maintenance_cost_year', label: 'Maintenance / Year',req: false, type: 'number', hints: ['maintenance','maintenance cost','maintenance cost year','annual maintenance'] },
  { key: 'electricity_rate',      label: 'Electricity Rate',  req: false, type: 'number', hints: ['electricity rate','electricity','power rate','kwh rate'] },
];

const MOULD_COLS = [
  { key: 'ref',                    label: 'Ref',           req: false, type: 'text',   hints: ['ref','reference','mould ref','mould no','tool no','tool number','mold no'] },
  { key: 'name',                   label: 'Name',          req: false, type: 'text',   hints: ['name','mould name','description','tool name','mold name'] },
  { key: 'cavities',               label: 'Cavities',      req: false, type: 'number', hints: ['cavities','cavity','no of cavities','number of cavities','cav'] },
  { key: 'toolchange_mount_min',   label: 'Mount (min)',   req: false, type: 'number', hints: ['mount','mount time','mounting time','toolchange mount','toolchange_mount_min'] },
  { key: 'toolchange_unmount_min', label: 'Unmount (min)', req: false, type: 'number', hints: ['unmount','unmount time','unmounting time','toolchange unmount','toolchange_unmount_min'] },
];

const COLS = { machine: MACHINE_COLS, mould: MOULD_COLS };

const MACHINE_PREVIEW = ['name', 'site', 'tonnes', 'screw_mm', 'year'];
const MOULD_PREVIEW   = ['ref', 'name', 'cavities', 'toolchange_mount_min', 'toolchange_unmount_min'];
const PREVIEW_KEYS    = { machine: MACHINE_PREVIEW, mould: MOULD_PREVIEW };

// ── helpers ───────────────────────────────────────────────────────────────────

function guessColumn(headers, hints) {
  const lower = headers.map(h => String(h).toLowerCase().trim());
  for (const hint of hints) {
    const idx = lower.findIndex(h => h === hint || h.includes(hint));
    if (idx !== -1) return headers[idx];
  }
  return null;
}

// ── preview table ─────────────────────────────────────────────────────────────

function PreviewTable({ rows, onChange, colDefs }) {
  function toggle(i) {
    onChange(rows.map((r, idx) => idx === i ? { ...r, _skip: !r._skip } : r));
  }
  function edit(i, field, val) {
    onChange(rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  }
  const active = rows.filter(r => !r._skip);

  return (
    <div>
      <div style={{ fontSize: '12px', color: MUTED, marginBottom: '8px' }}>
        {active.length} of {rows.length} rows selected — uncheck to skip
      </div>
      <div style={{ overflowX: 'auto', maxHeight: '380px', overflowY: 'auto',
        border: `1px solid ${BORDER}`, borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead style={{ position: 'sticky', top: 0, background: '#161b22' }}>
            <tr>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: MUTED, width: '32px' }} />
              {colDefs.map(c => (
                <th key={c.key} style={{ padding: '8px 10px', textAlign: 'left', color: MUTED, whiteSpace: 'nowrap' }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${BORDER}22`, opacity: row._skip ? 0.35 : 1 }}>
                <td style={{ padding: '6px 10px' }}>
                  <input type="checkbox" checked={!row._skip} onChange={() => toggle(i)}
                    style={{ accentColor: TEAL, width: '14px', height: '14px', cursor: 'pointer' }} />
                </td>
                {colDefs.map(c => (
                  <td key={c.key} style={{ padding: '4px 6px' }}>
                    {c.type === 'bool' ? (
                      <select value={row[c.key] || ''} onChange={e => edit(i, c.key, e.target.value)}
                        style={{ ...inp, padding: '4px 8px', width: '80px' }}>
                        <option value="">—</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    ) : (
                      <input value={row[c.key] || ''} onChange={e => edit(i, c.key, e.target.value)}
                        style={{
                          ...inp, padding: '4px 8px',
                          width: ['name'].includes(c.key) ? '180px' : ['ref','site','drive_type','platen_type','hot_runner_brand'].includes(c.key) ? '120px' : '80px',
                        }}
                        placeholder="—" />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Excel tab ─────────────────────────────────────────────────────────────────

function ExcelTab({ type, onReady }) {
  const fileRef  = useRef();
  const cols     = COLS[type];
  const [headers,     setHeaders]     = useState([]);
  const [rawRows,     setRawRows]     = useState([]);
  const [mapping,     setMapping]     = useState({});
  const [previewRows, setPreviewRows] = useState(null);
  const [error,       setError]       = useState('');

  function handleFile(f) {
    if (!f) return;
    setError('');
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const wb   = XLSX.read(e.target.result, { type: 'array' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
        if (!rows.length) { setError('Empty sheet'); return; }
        const hdr  = rows[0].map(h => h == null ? '' : String(h));
        const data = rows.slice(1).filter(r => r.some(c => c != null));
        setHeaders(hdr);
        setRawRows(data);
        const auto = {};
        cols.forEach(c => {
          const m = guessColumn(hdr, c.hints);
          if (m) auto[c.key] = m;
        });
        setMapping(auto);
        setPreviewRows(null);
        setError('');
      } catch (err) {
        setError('Could not parse file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(f);
  }

  function buildPreview() {
    if (type === 'machine' && !mapping.name) { setError('Select the Name column first'); return; }
    if (type === 'mould'   && !mapping.ref && !mapping.name) { setError('Select at least Ref or Name column'); return; }

    const idxOf = key => (mapping[key] ? headers.indexOf(mapping[key]) : -1);

    const rows = rawRows.map(r => {
      const obj = { _skip: false };
      cols.forEach(c => {
        const idx = idxOf(c.key);
        obj[c.key] = idx >= 0 && r[idx] != null ? String(r[idx]).trim() : '';
      });
      return obj;
    }).filter(r => type === 'machine' ? r.name : (r.ref || r.name));

    if (!rows.length) { setError('No valid rows found'); return; }
    setError('');
    setPreviewRows(rows);
  }

  const previewCols = PREVIEW_KEYS[type];
  const colDefs     = cols.filter(c => previewCols.includes(c.key));
  const noun        = type === 'machine' ? 'machines' : 'moulds';

  if (previewRows) {
    return (
      <div>
        <button onClick={() => setPreviewRows(null)}
          style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px',
            cursor: 'pointer', padding: '0 0 12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back to mapping
        </button>
        <PreviewTable rows={previewRows} onChange={setPreviewRows} colDefs={colDefs} />
        <button onClick={() => onReady(previewRows.filter(r => !r._skip))}
          style={{ marginTop: '16px', background: TEAL, border: 'none', color: '#fff',
            borderRadius: '8px', padding: '10px 24px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          Import {previewRows.filter(r => !r._skip).length} {noun}
        </button>
      </div>
    );
  }

  const selStyle = { ...inp };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '11px', fontWeight: '600', color: MUTED, textTransform: 'uppercase',
          letterSpacing: '0.05em', marginBottom: '6px' }}>Upload file</div>
        <div style={{ fontSize: '11px', color: MUTED, marginBottom: '10px' }}>
          Row 1 = column headers, rows below = data. Supported: .xlsx, .xls, .csv
        </div>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files[0])} />
        <button onClick={() => fileRef.current.click()}
          style={{ background: '#161b22', border: `1px solid ${BORDER}`, color: TEXT,
            borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' }}>
          <i className="fa-solid fa-upload" style={{ marginRight: '8px', color: TEAL }} />
          Choose file…
        </button>
      </div>

      {headers.length > 0 && (
        <div>
          <div style={{ fontSize: '13px', color: MUTED, marginBottom: '16px' }}>
            {rawRows.length} data rows detected. Map your columns:
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: type === 'machine' ? 'repeat(2, 1fr)' : '1fr',
            gap: '12px 28px',
            marginBottom: '20px',
          }}>
            {cols.map(c => (
              <div key={c.key}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: MUTED, textTransform: 'uppercase',
                  letterSpacing: '0.05em', marginBottom: '4px' }}>
                  {c.label}{c.req && <span style={{ color: RED }}> *</span>}
                </div>
                <select value={mapping[c.key] || ''} onChange={e => setMapping(m => ({ ...m, [c.key]: e.target.value }))}
                  style={selStyle}>
                  <option value="">— none —</option>
                  {headers.filter(Boolean).map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            ))}
          </div>
          {error && <div style={{ color: RED, fontSize: '12px', marginBottom: '10px' }}>{error}</div>}
          <button onClick={buildPreview}
            style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '8px',
              padding: '9px 22px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            Preview mapping
          </button>
        </div>
      )}

      {!headers.length && error && (
        <div style={{ color: RED, fontSize: '12px', marginTop: '10px' }}>{error}</div>
      )}
    </div>
  );
}

// ── result screen ─────────────────────────────────────────────────────────────

function ResultScreen({ result, type, onImportMore, onGoBack }) {
  const label = type === 'machine' ? 'Machines' : 'Moulds';
  const noun  = type === 'machine' ? 'machines' : 'moulds';
  return (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <div style={{ fontSize: '40px', marginBottom: '16px' }}>
        <i className="fa-solid fa-circle-check" style={{ color: TEAL }} />
      </div>
      <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '6px' }}>Import complete</div>
      <div style={{ color: MUTED, fontSize: '13px', marginBottom: '6px' }}>
        <span style={{ color: TEAL, fontWeight: '700' }}>{result.created?.length || 0}</span> {noun} added to catalogue
      </div>
      {result.skippedCount > 0 && (
        <div style={{ color: MUTED, fontSize: '12px', marginBottom: '24px' }}>
          {result.skippedCount} skipped — already exists
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={onImportMore}
          style={{ background: CARD, border: `1px solid ${BORDER}`, color: TEXT,
            borderRadius: '8px', padding: '9px 20px', fontSize: '13px', cursor: 'pointer' }}>
          Import more
        </button>
        <button onClick={onGoBack}
          style={{ background: TEAL, border: 'none', color: '#fff',
            borderRadius: '8px', padding: '9px 20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          Back to {label}
        </button>
      </div>
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function AdminCatalogImport({ type }) {
  const navigate  = useNavigate();
  const goBack    = useBack('/admin');
  const [importing, setImporting] = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState('');

  const isMachine = type === 'machine';
  const label     = isMachine ? 'Machines' : 'Moulds';
  const backPath  = isMachine ? '/admin/machines' : '/admin/moulds';
  const apiPath   = isMachine ? '/api/v1/catalog/machines' : '/api/v1/catalog/moulds';
  const noun      = isMachine ? 'machines' : 'moulds';

  async function handleImport(rows) {
    if (!rows.length) return;
    setImporting(true);
    setError('');
    try {
      const { data } = await api.post(apiPath, { rows });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setImporting(false);
    }
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: BG, minHeight: '100vh' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <button onClick={goBack}
          style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px', cursor: 'pointer',
            padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
        </button>
        <span style={{ color: BORDER }}>·</span>
        <button onClick={() => navigate(backPath)}
          style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px', cursor: 'pointer', padding: 0 }}>
          {label}
        </button>
        <span style={{ color: BORDER }}>·</span>
        <span style={{ fontSize: '12px', color: TEXT }}>Import</span>
      </div>

      <h1 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '4px', marginTop: '8px' }}>
        Import {label}
      </h1>
      <p style={{ color: MUTED, marginBottom: '24px', fontSize: '13px' }}>
        {isMachine
          ? 'Bulk-add machines from Excel — fill in detailed parameters manually afterwards'
          : 'Bulk-add moulds from Excel — fill in gate type, shot weight, and other parameters manually afterwards'}
      </p>

      {result ? (
        <div style={{ maxWidth: '520px', background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '24px' }}>
          <ResultScreen result={result} type={type} onImportMore={() => setResult(null)} onGoBack={() => navigate(backPath)} />
        </div>
      ) : (
        <div style={{ maxWidth: '800px', background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '24px' }}>
          {importing ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: MUTED, fontSize: '13px' }}>
              <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }} />
              Importing {noun}…
            </div>
          ) : (
            <ExcelTab type={type} onReady={handleImport} />
          )}
          {error && (
            <div style={{ color: RED, fontSize: '12px', marginTop: '16px',
              background: RED + '15', border: `1px solid ${RED}33`, borderRadius: '8px', padding: '10px 14px' }}>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
